import { Box, Stack, Typography } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import { DecoratedSchedulerOption } from "./SchedulerScreen";
import { rankColor } from "~/utils/scheduleOptions";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { useTheme } from "@mui/material/styles";
import { Palette, PaletteColor } from "@mui/material/styles";
type PaletteColorKeys = keyof Palette;

type Props = {
  options: DecoratedSchedulerOption[];
  onChange: (option: DecoratedSchedulerOption) => void;
  selectedOption: DecoratedSchedulerOption | null;
  selectedDate: Date | null;
  providersById: Record<number, {id: number, firstName: string, lastName: string}>
  timezone: string
};

function getOptionKey(option: DecoratedSchedulerOption | null): string {
  if (!option) return ""
  return option.resources.map((r) => `${r.startTime}-${r.endTime}-${r.providerId}`).join('|')
}

export default function SchedulerAvailableTimes({
  options,
  onChange,
  selectedOption,
  selectedDate,
  providersById,
  timezone
}: Props) {
  if (!options?.length) {
    null;
  }

  const { timeRangeLabel, timeLabel, datef } = useZonedDateFormat();

  return (
    <Box>
      {selectedDate && (
        <Typography variant="h6" gutterBottom>
          {datef(selectedDate, "EEEE MMMM d")} - All Available Options
        </Typography>
      )}
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        {options?.map((option) => {
          const primaryResource = option.resources.find((resource) => resource.inHome) || option.resources?.[0]
          const providerInitials = primaryResource
            ? `${providersById[primaryResource.providerId]?.firstName[0]}${providersById[primaryResource.providerId]?.lastName[0]}`
            : "";

          // If the arrival window is zero offset, show the time range as the start and end times
          const zeroOffsetArrivalWindow = new Date(option.arrivalWindowStart).getTime() === new Date(option.arrivalWindowEnd).getTime();
          const time = zeroOffsetArrivalWindow ?
            timeLabel(option.startTime, true, timezone) :
            timeRangeLabel(option.arrivalWindowStart, option.arrivalWindowEnd, false, false, timezone);
          return (
            <TimeSlot
              key={getOptionKey(option)}
              time={time}
              avatarLabel={providerInitials}
              selected={getOptionKey(option) === getOptionKey(selectedOption)}
              rank={option.rankCategory}
              onClick={() => onChange(option)}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

type TimeSlotProps = {
  time: string;
  avatarLabel: string;
  selected: boolean;
  rank: string;
  onClick?: () => void;
};

function TimeSlot({
  time,
  avatarLabel,
  selected,
  rank,
  onClick,
}: TimeSlotProps) {
  const theme = useTheme();
  const chipColor = (rankColor(rank) as PaletteColorKeys) || "success";
  const paletteColor =
    chipColor && chipColor in theme.palette
      ? (theme.palette[chipColor] as PaletteColor)
      : theme.palette.success;
  const avatarStyles = {
    bgcolor: selected ? theme.palette.grey[100] : theme.palette.grey[600],
    color: selected ? theme.palette.grey[600] : theme.palette.grey[100],
    width: 24,
    height: 24,
    fontSize: "0.8rem",
    flexShrink: 0,
    marginLeft: "auto",
  };

  return (
    <Box
      role="button"
      aria-label={`${time} ${avatarLabel}`}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: selected ? paletteColor.light : "white",
        color: selected ? "white" : paletteColor.dark,
        border: `3px solid ${paletteColor.main}`,
        borderRadius: "16px",
        padding: "8px 12px",
        cursor: "pointer",
        width: "11rem",
        fontSize: "1.0rem",
        fontWeight: 800,
      }}
    >
      <Typography
        sx={{
          flexGrow: 1,
          textAlign: "left",
          marginRight: "8px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: 1000,
        }}
      >
        {time}
      </Typography>
      <Avatar sx={avatarStyles}>{avatarLabel}</Avatar>
    </Box>
  );
}
